// Client wrapper around the /api/answer-cleanup endpoint.
//
// Called from AnswerScreen after dictation finishes. Sends the raw transcript, the
// question text, and the worksheet's full text. Returns either the cleaned answer
// or the original (the endpoint fails open so the user is never blocked).

import { isCloudVisionEnabled } from './vision';

export interface CleanupRequest {
  transcript: string;
  questionText: string;
  worksheetContext?: string;
  expectedFormat?: 'number' | 'word' | 'short_phrase' | 'sentence' | 'paragraph';
  options?: string[];
}

export interface CleanupResult {
  cleaned: string;
  changed: boolean;
  reason?: string;
}

export async function cleanupAnswer(req: CleanupRequest): Promise<CleanupResult> {
  // Respect the cloud opt-out: cleanup is a cloud call, so we skip it when the user
  // has disabled cloud AI in Settings.
  if (!isCloudVisionEnabled()) {
    return { cleaned: req.transcript, changed: false, reason: 'cloud_off' };
  }

  // Nothing to clean if there's no transcript.
  if (!req.transcript.trim()) {
    return { cleaned: '', changed: false, reason: 'empty' };
  }

  try {
    const response = await fetch('/api/answer-cleanup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(req),
    });
    if (!response.ok) {
      return { cleaned: req.transcript, changed: false, reason: `http_${response.status}` };
    }
    const json = (await response.json()) as { cleaned?: string; changed?: boolean; reason?: string };
    return {
      cleaned: typeof json.cleaned === 'string' && json.cleaned ? json.cleaned : req.transcript,
      changed: Boolean(json.changed),
      reason: typeof json.reason === 'string' ? json.reason : undefined,
    };
  } catch {
    return { cleaned: req.transcript, changed: false, reason: 'network' };
  }
}
